import { TravelInsuranceQuotesData } from '../../types/travelInsurance';

/**
 * Whether a travel insurance quote is still resolving, so nothing may be
 * rendered that depends on a premium.
 *
 * Q7 in the sort-and-filter thread (Clara, 27 Aug) and CMS-1542 R12.3: no plan
 * renders before its premium exists, and the results bar must not name a sort
 * it has not applied. Both callers ask the same question, so it is answered in
 * one place — the results bar CMS-1542 adds reads this too.
 *
 * `undefined` is not pending: every non-travel vertical passes no quote at all,
 * and treating that as "still loading" would replace those pages with
 * skeletons forever.
 */
export const arePremiumsPending = (
  travelQuotes: TravelInsuranceQuotesData | undefined,
): boolean => travelQuotes?.status === 'inprogress';

/**
 * How many skeleton rows stand in for the results.
 *
 * The wrapper's threshold is the intended row count while a quote runs. The
 * products already on the page are the fallback, so a block whose threshold is
 * unset keeps its height instead of collapsing; one row is the floor, because
 * rendering none is the empty container Q7 rules out.
 */
export const pendingSkeletonCount = (
  skeletonThreshold: number | undefined,
  renderableCount: number,
): number => skeletonThreshold || renderableCount || 1;

/**
 * Whether the quote produced no usable answer, so the results bar must say
 * nothing at all.
 *
 * A failed or timed-out quote prices no plan, so the count would read "No
 * plans found" beside an error panel already explaining that the search broke
 * — two messages for one event. Clara settled it on CMS-1542 (comment 219576):
 * that copy belongs to a quote that succeeded and returned zero.
 *
 * Deliberately separate from `arePremiumsPending` rather than folded into it:
 * that one also drives the skeleton rows, and a failure that reads as "still
 * loading" leaves the column in skeletons for good.
 */
export const isQuoteUnresolved = (
  travelQuotes: TravelInsuranceQuotesData | undefined,
): boolean =>
  arePremiumsPending(travelQuotes) ||
  travelQuotes?.status === 'fail' ||
  travelQuotes?.status === 'timeout';
